<template>
  <v-menu
    v-model="internalOpen"
    bottom
    max-height="380"
    max-width="260"
    min-width="260"
    offset-y
    right
    :close-on-content-click="false"
    :z-index="zIndex"
  >
    <template v-slot:activator="{ on }">
      <span v-on="disabled ? null : on" class="width-fit-content">
        <slot></slot>
      </span>
    </template>

    <v-sheet color="white" class="pa-0">
      <slot name="prepend"> </slot>

      <div class="entity-search-popover">
        <div class="entity-search-popover__sticky-top">
          <SearchScopeSelector
            v-if="allowSearchScopeChange && !localFilter"
            class="pt-3 px-2"
            data-tourid="search-scope-selector"
            :disabled="loading || disabled"
            :scope="searchScope"
            @change="$emit('searchScopeChange', $event)"
          />

          <v-text-field
            v-model="searchTerm"
            autofocus
            class="entity-search-popover__search pa-2 mt-0"
            clearable
            dense
            hide-details
            prepend-inner-icon="mdi-magnify"
            ref="searchInput"
            :disabled="loading"
            :placeholder="placeholder || $t('common.search')"
          />
          <v-divider />

          <div v-if="loading" class="search-popover-skeletons">
            <v-skeleton-loader class="my-3 mx-4" type="text" />
            <v-skeleton-loader
              type="list-item-avatar-two-line,
                    list-item-avatar-two-line,
                    list-item-avatar-two-line,
                    list-item-avatar-two-line,
                    list-item-avatar-two-line,
                    list-item-avatar-two-line,
                    list-item-avatar-two-line,
                    list-item-avatar-two-line"
            />
          </div>
        </div>

        <template v-if="searchTerm">
          <v-list
            v-if="computedMatchingItems.length && !loading"
            class="pa-1"
            color="transparent"
            dense
          >
            <div v-for="item in computedMatchingItems" :key="getItemKey(item)">
              <slot
                name="item"
                :item="item"
                :searchTerm="searchTerm"
                :isSelected="isSelected(item)"
              >
                <v-list-item @click="handleSelect(item)">
                  <v-list-item-content>
                    <v-list-item-title>{{
                      getItemTitle(item)
                    }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </slot>
            </div>
          </v-list>
        </template>

        <template v-else>
          <v-list
            v-if="selectedItems && selectedItems.length && !loading"
            class="pa-1"
            color="transparent"
            data-tourid="entity-selector-selected"
            dense
          >
            <div class="px-3 py-2 d-flex">
              <span
                class="caption blue-grey--text text--darken-1 font-weight-500"
              >
                {{ $t('common.selected') }}
                <template v-if="multiple">
                  ({{ selectedItems.length }})
                </template>
              </span>

              <span
                v-if="allowEmptySelection"
                class="ml-auto pl-2 cursor-pointer caption primary--text font-weight-500"
                @click="onClearAll"
              >
                {{ $t('common.clear_all') }}
              </span>
            </div>

            <div v-for="item in selectedItems" :key="getItemKey(item)">
              <slot
                name="selectedItem"
                :isSelected="true"
                :item="item"
                :searchTerm="searchTerm"
              >
                <v-list-item @click="handleRemove(item)">
                  <v-list-item-content>
                    <v-list-item-title>{{
                      getItemTitle(item)
                    }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </slot>
            </div>
          </v-list>

          <v-list
            v-if="unselectedItems.length && !loading"
            class="pa-1"
            color="transparent"
            dense
          >
            <div
              class="caption blue-grey--text text--darken-1 font-weight-500 px-3 py-2"
            >
              {{ resultsHeaderText || $t('common.result_other') }}

              <span v-if="searchTerm">({{ unselectedItems.length }})</span>
            </div>

            <div v-for="item in unselectedItems" :key="getItemKey(item)">
              <slot
                name="item"
                :item="item"
                :searchTerm="searchTerm"
                :isSelected="isSelected(item)"
              >
                <v-list-item @click="handleSelect(item)">
                  <v-list-item-content>
                    <v-list-item-title>{{
                      getItemTitle(item)
                    }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </slot>
            </div>
          </v-list>
        </template>

        <div
          v-if="showAdvancedOption && unselectedItems.length && !loading"
          class="entity-search-popover__sticky-bottom"
          data-tourid="entity-selector-advance-option"
        >
          <Button
            plain
            class="pa-5"
            color="primary"
            :text="$t('common.advanced_selection')"
            @click="handleOpenAdvanced"
          />
        </div>
        <div
          v-if="!computedMatchingItems.length && !loading"
          class="caption pa-4 blue-grey--text"
        >
          {{ $t('common.no_results_found') }}
        </div>
      </div>
    </v-sheet>
  </v-menu>
</template>

<script>
import _ from 'lodash'

import { fuseSearch } from '@/lib/fuseSearch'
import constants from '@/constants'

import Button from '@/components/UI/Button'
import SearchScopeSelector from '@/components/SearchScopeSelector.vue'

export default {
  name: 'EntitySearchPopover',

  components: { Button, SearchScopeSelector },

  props: {
    open: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    zIndex: { type: Number, default: 203 },
    items: { type: Array, default: () => [] },
    selectedItems: { type: Array, default: () => [] },
    matchingItems: { type: Array, default: () => [] },
    itemKey: { type: String, default: 'id' },
    blacklist: { type: Array, default: () => [] },
    allowSearchScopeChange: { type: Boolean, default: true },
    searchScope: {
      type: String,
      validator: value =>
        Object.values(constants.SEARCH_SCOPES).includes(value),
      default: constants.SEARCH_SCOPES.site,
    },
    multiple: { type: Boolean, default: false },
    allowEmptySelection: { type: Boolean, default: true },
    maxSelectable: { type: Number, default: null },
    localFilter: { type: Boolean, default: false },
    localFilterFields: { type: Array, default: () => ['title', 'name'] },
    placeholder: { type: String, default: null },
    resultsHeaderText: { type: String, default: null },
    showAdvancedOption: { type: Boolean, default: false },
  },

  data() {
    return {
      internalOpen: this.open,
      searchTerm: '',
    }
  },

  watch: {
    open: {
      immediate: true,
      handler(isOpen) {
        this.internalOpen = isOpen
      },
    },

    internalOpen(isOpen) {
      this.$emit('openChange', isOpen)
    },

    searchTerm: _.debounce(function(term) {
      this.emitSearch(term)
    }, 500),

    async loading(isLoading) {
      if (!isLoading) {
        await this.$nextTick()
        this.$refs.searchInput?.$refs?.input?.focus?.()
      }
    },
  },

  computed: {
    selectedKeysSet() {
      return new Set(
        (this.selectedItems || []).map(item => this.getItemKey(item))
      )
    },

    blacklistSet() {
      return new Set(this.blacklist || [])
    },

    unselectedItems() {
      return (this.items || []).filter(
        item =>
          !this.selectedKeysSet.has(this.getItemKey(item)) &&
          !this.blacklistSet.has(this.getItemKey(item))
      )
    },

    computedMatchingItems() {
      if (this.localFilter) {
        return fuseSearch(
          this.items || [],
          this.localFilterFields,
          this.searchTerm
        )
      }

      return this.matchingItems || []
    },
  },

  methods: {
    getItemKey(item) {
      return item?.[this.itemKey]
    },

    isSelected(item) {
      return (
        this.selectedKeysSet.has(this.getItemKey(item)) &&
        (this.multiple || this.allowEmptySelection)
      )
    },

    getItemTitle(item) {
      return item?.title ?? item?.name ?? ''
    },

    emitSearch(term) {
      this.$emit('search', term)
    },

    handleSelect(item) {
      if (!this.multiple) {
        this.$emit('select', item)
        this.internalOpen = false
        this.searchTerm = ''
        return
      }

      if (
        this.maxSelectable &&
        (this.selectedItems?.length || 0) >= this.maxSelectable
      ) {
        this.$emit('limitExceeded', {
          limit: this.maxSelectable,
          totalSelected: this.selectedItems.length,
        })

        return
      }

      this.$emit('select', item)
    },

    handleRemove(item) {
      this.$emit('remove', item)
    },

    onClearAll() {
      this.$emit('clearAll')
    },

    handleOpenAdvanced() {
      this.$emit('showAdvancedSearch')
      this.internalOpen = false
    },
  },
}
</script>

<style lang="scss">
.entity-search-popover {
  .entity-search-popover__search {
    font-size: 12px !important;
    .v-input__slot::after {
      border-style: none !important;
    }
    .v-input__slot::before {
      border-style: none !important;
    }
    .v-input__slot .v-text-field__slot input {
      padding-top: 0;
    }
    .v-input__append-inner button.v-icon {
      color: $color-blue-grey !important;
    }
    .v-input__prepend-inner i.v-icon {
      color: $color-blue-grey;
    }
  }
}
.search-popover-skeletons
  .v-skeleton-loader
  .v-skeleton-loader__list-item-avatar-two-line {
  height: 40px;
  margin: 4px 0;
  .v-skeleton-loader__avatar {
    margin-left: 4px;
    margin-right: 8px;
    width: 24px;
    height: 24px;
  }
}
.entity-search-popover__sticky-bottom {
  position: sticky;
  bottom: 0;
  border-top: 1px solid $color-blue-grey-lighten2;
  background-color: $color-white;
}
.entity-search-popover__sticky-top {
  position: sticky;
  top: 0;
  background-color: $color-white;
  z-index: 5;
}
</style>
